import { atom, map } from "nanostores";
import type { AirmetCollection, NotamRow } from "./api";
import type { Airport, FlightRecord, LivePlane, SignalStatus, Theme, Weather } from "./types";

function safeGetItem(key: string): string | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}
const storedTheme = safeGetItem("pifeeder-theme") as Theme | null;
export const $theme = atom<Theme>(storedTheme ?? "dark");
$theme.subscribe((t) => {
  if (typeof document !== "undefined") {
    document.documentElement.classList.toggle("theme-light", t === "light");
  }
  try {
    localStorage.setItem("pifeeder-theme", t);
  } catch {
    // localStorage unavailable (private mode / embedded / test env)
  }
});

/** Currently-focused airport (ICAO). The whole right rail + map highlighting
 *  reacts to this. Set by clicking an airport pin on the map, or auto-set
 *  to the nearest airport whenever the station location resolves (until the
 *  user makes a manual pick — see `$icaoUserPicked`). RJTT is the initial
 *  fallback for the Setagaya default station. */
export const $currentIcao = atom<string>("RJTT");

/** Sticky flag: true once the user has explicitly picked an airport (by
 *  clicking a pin or the search UI). Once set, station-driven nearest-
 *  airport auto-selection stops, so the user's pick is never overwritten. */
export const $icaoUserPicked = atom<boolean>(false);

export const $signal = atom<SignalStatus>({ online: true, dB: 47.4, trackedCount: 0 });

export const $planes = map<Record<string, LivePlane>>({});
export const $selectedId = atom<string | null>(null);

/** Recent positions per plane (id → ring buffer). Used to render the
 *  altitude-coloured polyline trail behind the selected aircraft. */
export interface TrailPoint {
  lat: number;
  lon: number;
  alt: number;
  ts: number;
}
export const $trails = map<Record<string, TrailPoint[]>>({});

/** Append the current position of every plane in `planes` to the trail
 *  buffer. Trails are kept for the full lifetime of the plane in the feed
 *  — when a plane disappears (no longer included in the next `planes`
 *  call), its trail is dropped automatically because we rebuild `next`
 *  from the current set.
 *
 *  Hard caps (see SCALING_AUDIT.md T-03 / B-11):
 *    - TRAIL_MAX_POINTS: most aircraft we'll ever care about visualising
 *      in a single session is ~1000 samples (= ~17 min at 1 Hz). The map
 *      can't draw more than that legibly anyway; older points are pruned
 *      from the head.
 *    - TRAIL_MAX_AGE_MS: drop samples older than 30 min regardless of
 *      count, so a plane that lingers (e.g. holding pattern) doesn't
 *      build a tangle of overlapping segments.
 *  Without these caps, 200 simultaneous planes × 1 Hz × multi-hour
 *  session → hundreds of MB of trail buffer and a browser tab OOM. */
const TRAIL_MAX_POINTS = 1000;
const TRAIL_MAX_AGE_MS = 30 * 60 * 1000;

export function recordTrails(planes: LivePlane[]) {
  const now = Date.now();
  const ageCutoff = now - TRAIL_MAX_AGE_MS;
  const cur = $trails.get();
  const next: Record<string, TrailPoint[]> = {};
  for (const p of planes) {
    const prev = cur[p.id] ?? [];
    const last = prev.at(-1);
    let updated: TrailPoint[];
    if (!last || last.lat !== p.lat || last.lon !== p.lon) {
      updated = prev.slice();
      updated.push({ lat: p.lat, lon: p.lon, alt: p.alt, ts: now });
    } else {
      updated = prev;
    }
    // Age pruning: drop samples older than TRAIL_MAX_AGE_MS. Trails are
    // append-only with monotonic ts, so a single from-the-front scan
    // finds the cut index.
    if (updated.length > 0 && updated[0]!.ts < ageCutoff) {
      let firstFresh = 0;
      while (firstFresh < updated.length && updated[firstFresh]!.ts < ageCutoff) {
        firstFresh++;
      }
      updated = updated.slice(firstFresh);
    }
    // Length cap: keep the most recent TRAIL_MAX_POINTS samples.
    if (updated.length > TRAIL_MAX_POINTS) {
      updated = updated.slice(updated.length - TRAIL_MAX_POINTS);
    }
    next[p.id] = updated;
  }
  $trails.set(next);
}

export const $flights = atom<FlightRecord[]>([]);
export const $weather = atom<Weather | null>(null);
export const $airport = atom<Airport | null>(null);
export const $notams = atom<NotamRow[]>([]);
export const $airmet = atom<AirmetCollection | null>(null);

export const $airportPanelVisible = atom<boolean>(true);

export interface PanelToggles {
  weather: boolean;
  runways: boolean;
  wind: boolean;
}
const storedToggles = safeGetItem("pifeeder-toggles");
const initialToggles: PanelToggles = storedToggles
  ? JSON.parse(storedToggles)
  : { weather: true, runways: true, wind: true };
export const $toggles = atom<PanelToggles>(initialToggles);
$toggles.subscribe((t) => {
  try {
    localStorage.setItem("pifeeder-toggles", JSON.stringify(t));
  } catch {
    // localStorage unavailable
  }
});

/** Pin a selection so external updates (e.g. mock feed switching focus) can't change it. */
export const $pinned = atom<boolean>(false);

/** Station location (the user's feeder rig). Falls back to Setagaya. */
export const $station = atom<{
  lat: number;
  lon: number;
  source: "default" | "geo" | "pi";
}>({
  lat: 35.6434,
  lon: 139.6534,
  source: "default",
});

export function selectPlane(id: string | null) {
  $selectedId.set(id);
}

export function upsertPlane(p: LivePlane) {
  $planes.setKey(p.id, p);
}

export function replacePlanes(planes: LivePlane[]) {
  const next: Record<string, LivePlane> = {};
  for (const p of planes) next[p.id] = p;
  $planes.set(next);
  $signal.set({ ...$signal.get(), trackedCount: planes.length });
}

export function removePlane(id: string) {
  const cur = $planes.get();
  if (!cur[id]) return;
  const next = { ...cur };
  delete next[id];
  $planes.set(next);
}
